/**
 * @file /app/LandingPageContent.tsx
 *
 * @description Hirecarta Landing Page: Story-Driven Narrative Redesign
 *
 * Sections:
 *   A  : Hero (cool off-white, confrontational headline, gold CTA)
 *   A.5: ATS screening (knockouts, keywords, competencies)
 *   B  : Master Profile (coaching photo + two-panel comparison)
 *   C  : Extension autofill video
 *   C.2: Application tracker
 *   D  : The Complete Suite (bento grid)
 *   D.5: Founder Video
 *   E  : Resume Check CTA block (routes to /resume-check)
 *   F  : The Close (steel blue full-bleed)
 *
 * Testimonial interstitials are placed between sections.
 *
 * Analytics: All scroll-depth, section-view, and CTA tracking preserved from v4.
 */

"use client";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import UpcomingEventsSection from "@/components/events/UpcomingEventsSection";
import { TestimonialInterstitial } from "@/components/landing/LandingTestimonials";
import { LandingBento } from "@/components/landing/LandingBento";
import { LandingAtsScreens } from "@/components/landing/LandingAtsScreens";
import { LandingExtensionFlow } from "@/components/landing/LandingExtensionFlow";
import { LandingTrackerBoard } from "@/components/landing/LandingTrackerBoard";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/logger-client";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import Image from "next/image";
import Link from "next/link";

// ---------------------------------------------------------------------------
// Gold CTA Button: reused across sections
// ---------------------------------------------------------------------------
function GoldCtaButton({
  label,
  eventLabel,
  buttonPosition,
  heroVariant,
}: {
  label: string;
  eventLabel: string;
  buttonPosition: string;
  heroVariant?: string | boolean | null;
}) {
  return (
    <button
      className="inline-flex items-center justify-center rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 bg-accent hover:bg-[#b87f00] text-white"
      style={{
        fontSize: "1.125rem",
        height: "3.25rem",
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        boxShadow: "0 2px 12px rgba(214,148,0,0.30)",
      }}
      onClick={() => {
        trackEvent("cta_click", {
          event_category: "conversion",
          event_label: eventLabel,
          page_location: window.location.href,
          button_text: label,
          button_position: buttonPosition,
          ab_variant: heroVariant ?? "control",
        });
        window.open("/login", "_blank");
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section B: Two-panel comparison (coach / agent)
// ---------------------------------------------------------------------------
const coachItems = [
  "Sits with you to understand your story, goals, and direction",
  "Takes detailed notes on every role, skill, and achievement",
  "Helps you reflect on what you actually want from your next job",
  "Uses what it learns to write a compelling resume",
];

const agentItems = [
  "Tailors your resume for every single job posting, automatically",
  "Writes a fresh cover letter for each application, in your voice",
  "Autofills the application form so you're done in minutes",
  "Tracks every application, document, and follow-up automatically",
  "Preps you for every interview, not just the first one",
  "Available at 11pm when you're anxious about tomorrow's call",
];

function subscribeReducedMotion(callback: () => void) {
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  motionQuery.addEventListener("change", callback);
  return () => motionQuery.removeEventListener("change", callback);
}

function getReducedMotionSnapshot() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getReducedMotionServerSnapshot() {
  return false;
}

function CoachComparisonPanels() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot
  );

  useEffect(() => {
    if (prefersReducedMotion) return;

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  return (
    <div ref={containerRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Part 1: Just like a great coach */}
      <div
        className="rounded-2xl p-8 transition-all duration-500 hover:shadow-lg"
        style={{
          background: "#f0f6ff",
          border: "1px solid rgba(20, 63, 128, 0.08)",
          opacity: isVisible || prefersReducedMotion ? 1 : 0,
          transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(20px)",
          transition: prefersReducedMotion
            ? "none"
            : "opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease",
        }}
      >
        <p
          className="text-base font-bold mb-5"
          style={{
            color: "#143f80",
            opacity: isVisible || prefersReducedMotion ? 1 : 0,
            transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(8px)",
            transition: prefersReducedMotion
              ? "none"
              : "opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) 100ms",
          }}
        >
          Just like a great coach
        </p>
        <ul className="space-y-4">
          {coachItems.map((item, idx) => {
            const delay = 160 + idx * 95;
            return (
              <li
                key={item}
                className="flex items-start gap-3"
                style={{
                  opacity: isVisible || prefersReducedMotion ? 1 : 0,
                  transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(14px)",
                  transition: prefersReducedMotion
                    ? "none"
                    : `opacity 0.55s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.55s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
                }}
              >
                <span
                  className="mt-0.5 shrink-0 text-base font-bold inline-block"
                  style={{
                    color: "#143f80",
                    transform: isVisible || prefersReducedMotion ? "scale(1)" : "scale(0.4)",
                    transition: prefersReducedMotion
                      ? "none"
                      : `transform 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${delay + 60}ms`,
                  }}
                  aria-hidden
                >
                  ✓
                </span>
                <span className="text-base leading-relaxed" style={{ color: "#1e293b" }}>
                  {item}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Part 2: And then some */}
      <div
        className="rounded-2xl p-8 transition-all duration-500 hover:shadow-lg"
        style={{
          background: "#f0fff4",
          border: "1px solid rgba(5, 150, 105, 0.12)",
          opacity: isVisible || prefersReducedMotion ? 1 : 0,
          transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(20px)",
          transition: prefersReducedMotion
            ? "none"
            : "opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) 150ms, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) 150ms, box-shadow 0.3s ease",
        }}
      >
        <p
          className="text-base font-bold mb-5"
          style={{
            color: "#059669",
            opacity: isVisible || prefersReducedMotion ? 1 : 0,
            transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(8px)",
            transition: prefersReducedMotion
              ? "none"
              : "opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1) 220ms, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) 220ms",
          }}
        >
          And then some
        </p>
        <ul className="space-y-4">
          {agentItems.map((item, idx) => {
            const delay = 300 + idx * 85;
            return (
              <li
                key={item}
                className="flex items-start gap-3"
                style={{
                  opacity: isVisible || prefersReducedMotion ? 1 : 0,
                  transform: isVisible || prefersReducedMotion ? "translateY(0)" : "translateY(14px)",
                  transition: prefersReducedMotion
                    ? "none"
                    : `opacity 0.55s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.55s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
                }}
              >
                <span
                  className="mt-0.5 shrink-0 text-base font-bold inline-block"
                  style={{
                    color: "#059669",
                    transform: isVisible || prefersReducedMotion ? "scale(1)" : "scale(0.4)",
                    transition: prefersReducedMotion
                      ? "none"
                      : `transform 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${delay + 60}ms`,
                  }}
                  aria-hidden
                >
                  →
                </span>
                <span className="text-base leading-relaxed" style={{ color: "#1e293b" }}>
                  {item}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section D.5: Founder Video with PostHog Interaction Tracking
// ---------------------------------------------------------------------------
function FounderVideoPlayer() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);
  const hasTrackedPlayRef = useRef(false);
  const hasTrackedEndRef = useRef(false);

  const handlePlayEvent = () => {
    if (hasTrackedPlayRef.current) return;
    hasTrackedPlayRef.current = true;
    trackEvent("founder_video_played", {
      event_category: "video_engagement",
      event_label: "founder_video_play",
      video_id: "9982e6af-c6d0-4e0d-a438-4a46dd8275ba",
      video_title: "A message from the Hirecarta founder",
      page_location: window.location.href,
    });
  };

  const handleEndEvent = () => {
    if (hasTrackedEndRef.current) return;
    hasTrackedEndRef.current = true;
    trackEvent("founder_video_completed", {
      event_category: "video_engagement",
      event_label: "founder_video_complete",
      video_id: "9982e6af-c6d0-4e0d-a438-4a46dd8275ba",
      video_title: "A message from the Hirecarta founder",
      page_location: window.location.href,
    });
  };

  useEffect(() => {
    // 1. Listen for postMessage from Bunny Stream (Player.js format or raw events)
    const onMessage = (e: MessageEvent) => {
      if (typeof e.origin === "string" && !e.origin.includes("mediadelivery.net")) return;
      let payload = e.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (
        payload?.event === "play" ||
        payload?.method === "play" ||
        (payload?.context === "player.js" && payload?.event === "play")
      ) {
        handlePlayEvent();
      } else if (
        payload?.event === "ended" ||
        payload?.method === "ended" ||
        (payload?.context === "player.js" && payload?.event === "ended")
      ) {
        handleEndEvent();
      }
    };

    window.addEventListener("message", onMessage);

    // 2. Click detection fallback using hover and window blur
    const onWindowBlur = () => {
      if (isHoveredRef.current && document.activeElement === iframeRef.current) {
        handlePlayEvent();
      }
    };

    window.addEventListener("blur", onWindowBlur);

    // 3. Initialize Player.js via Bunny CDN library if available
    let playerInstance: any = null;
    const initPlayerJs = () => {
      if (typeof window !== "undefined" && (window as any).playerjs && iframeRef.current) {
        try {
          playerInstance = new (window as any).playerjs.Player(iframeRef.current);
          playerInstance.on("play", handlePlayEvent);
          playerInstance.on("ended", handleEndEvent);
        } catch {
          // Fail gracefully and rely on postMessage / blur fallbacks
        }
      }
    };

    if (typeof window !== "undefined" && (window as any).playerjs) {
      initPlayerJs();
    } else {
      const script = document.createElement("script");
      script.src = "https://assets.mediadelivery.net/playerjs/playerjs-latest.min.js";
      script.async = true;
      script.onload = () => initPlayerJs();
      document.head.appendChild(script);
    }

    // 4. Request player events subscription directly via postMessage once iframe loads
    const iframe = iframeRef.current;
    const sendSubscribe = () => {
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ context: "player.js", version: "0.0.11", method: "addEventListener", value: "play" }),
          "*"
        );
        iframe.contentWindow.postMessage(
          JSON.stringify({ context: "player.js", version: "0.0.11", method: "addEventListener", value: "ended" }),
          "*"
        );
      }
    };

    if (iframe) {
      iframe.addEventListener("load", sendSubscribe);
    }

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("blur", onWindowBlur);
      if (iframe) {
        iframe.removeEventListener("load", sendSubscribe);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="rounded-xl overflow-hidden mx-auto"
      style={{
        maxWidth: "640px",
        boxShadow: "0 4px 30px rgba(20,63,128,0.10)",
        position: "relative",
        paddingTop: "56.25%",
      }}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
    >
      <iframe
        ref={iframeRef}
        src="https://iframe.mediadelivery.net/embed/686769/9982e6af-c6d0-4e0d-a438-4a46dd8275ba?autoplay=false&loop=false&muted=false&preload=true"
        loading="lazy"
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
        allowFullScreen
        title="A message from the Hirecarta founder"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section D: Bento Grid (Uses cropped image from design brief)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function LandingPageContent() {
  const heroVariant = useFeatureFlagVariantKey("landing-hero-headline");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ── Scroll depth tracking ──────────────────────────────────────────────
  useEffect(() => {
    const trackedMilestones = new Set<number>();
    const trackedSections = new Set<string>();

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const scrollTop = container.scrollTop;
      const docHeight = container.scrollHeight - container.clientHeight;
      if (docHeight <= 0) return;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      [25, 50, 75, 100].forEach((milestone) => {
        if (scrollPercent >= milestone && !trackedMilestones.has(milestone)) {
          trackedMilestones.add(milestone);
          trackEvent("scroll_depth", {
            event_category: "engagement",
            event_label: `scroll_${milestone}_percent`,
            value: milestone,
            page_location: window.location.href,
          });
        }
      });
    };

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !trackedSections.has(entry.target.id)) {
            trackedSections.add(entry.target.id);
            trackEvent("section_view", {
              event_category: "content_engagement",
              event_label: entry.target.id,
              page_location: window.location.href,
              section_name: entry.target.id,
            });
          }
        });
      },
      { threshold: 0.3 },
    );

    const sectionIds = [
      "hero-section",
      "ats-screening-section",
      "coach-comparison-section",
      "extension-autofill-section",
      "application-tracker-section",
      "bento-features-section",
      "founder-video-section",
      "resume-check-banner-section",
      "final-cta-section",
    ];

    sectionIds.forEach((id) => {
      const el = document.querySelector(`#${id}`);
      if (el) sectionObserver.observe(el);
    });

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) scrollContainer.removeEventListener("scroll", handleScroll);
      sectionObserver.disconnect();
    };
  }, []);

  // ── Active engagement time tracking ───────────────────────────────────
  useEffect(() => {
    let activeTime = 0;
    let lastActiveTime = Date.now();
    let isWindowActive = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const handleActivity = () => { lastActiveTime = Date.now(); };
    const handleVisibilityChange = () => { isWindowActive = document.visibilityState === "visible"; };
    const handleFocus = () => { isWindowActive = true; };
    const handleBlur = () => { isWindowActive = false; };

    timer = setInterval(() => {
      const now = Date.now();
      if (isWindowActive && now - lastActiveTime < 5000) activeTime += 1;
    }, 1000);

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      if (activeTime >= 2) {
        trackEvent("landing_page_active_time", {
          active_seconds: activeTime,
          page_location: window.location.href,
        });
      }
    };
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: "#f2f5f9" }}>
      <Header activePage="home" />

      {/* Scrollable content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">

        {/* ================================================================
            SECTION A: HERO
            Background: cool off-white (#f4f6f9)
        ================================================================ */}
        <section
          id="hero-section"
          className="px-6 md:px-12"
          style={{
            background: "#f4f6f9",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left — copy */}
              <div className="space-y-8 max-w-2xl">
                <h1
                  className="font-bold leading-tight"
                  style={{
                    fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)",
                    color: "#143f80",
                    lineHeight: 1.12,
                    letterSpacing: "-0.02em",
                  }}
                >
                  You&apos;re not getting ghosted because you&apos;re underqualified.
                </h1>
                <p
                  className="leading-relaxed"
                  style={{
                    fontSize: "1.15rem",
                    color: "#334155",
                    maxWidth: "52ch",
                  }}
                >
                  You&apos;re getting ghosted because your resume looks like everyone else&apos;s.
                  Hirecarta fixes that, and then does the next 47 things you don&apos;t have time for.
                </p>
                <GoldCtaButton
                  label="Start Free (No Credit Card)"
                  eventLabel="hero_cta_button"
                  buttonPosition="hero_section"
                  heroVariant={heroVariant}
                />
              </div>

              {/* Right — hero image */}
              <div className="relative hidden lg:block">
                <Image
                  src="/images/hero-people.png"
                  alt="People using Hirecarta to land their dream jobs"
                  width={800}
                  height={600}
                  priority
                  className="w-full h-auto rounded-2xl"
                  style={{ boxShadow: "0 8px 40px rgba(20,63,128,0.12)" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial: After Section A */}
        <TestimonialInterstitial
          quote="I've actually been using Hirecarta exclusively since I discovered it. I have just been focusing on quality over quantity with my applications recently. I find the platform very useful and have already secured two interviews since I started using it."
          name="Lindsey C."
        />

        {/* ================================================================
            SECTION A.5: ATS SCREENING (knockouts, keywords, competencies)
            Background: cool off-white (#f4f6f9)
        ================================================================ */}
        <section
          id="ats-screening-section"
          className="px-6 md:px-12"
          style={{
            background: "#f4f6f9",
            paddingTop: "6rem",
            paddingBottom: "6rem",
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="font-bold mx-auto"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  color: "#143f80",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                  maxWidth: "28ch",
                }}
              >
                An Applicant Tracking System screens your resume 3 ways.
              </h2>
              <p
                className="mt-4 mx-auto"
                style={{ fontSize: "1.15rem", color: "#334155", maxWidth: "50ch", lineHeight: 1.55 }}
              >
                Hirecarta helps you fix all three, and shows you where you stand.
              </p>
            </div>

            <LandingAtsScreens />
          </div>
        </section>

        {/* ================================================================
            SECTION B: COACH COMPARISON
            Background: white
        ================================================================ */}
        <section
          id="coach-comparison-section"
          className="px-6 md:px-12"
          style={{
            background: "#ffffff",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-5xl mx-auto">
            {/* Headline */}
            <div className="text-center mb-12">
              <h2
                className="font-bold"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  color: "#143f80",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                }}
              >
                Most AI tools know nothing about you.
              </h2>
              <p
                className="mt-4 mx-auto"
                style={{
                  fontSize: "1.15rem",
                  color: "#334155",
                  maxWidth: "50ch",
                  lineHeight: 1.55,
                }}
              >
                <strong>Hirecarta</strong> starts where a great coach starts: <strong>your full story.</strong>
              </p>
            </div>

            {/* Coaching photo */}
            <div className="mb-14 rounded-2xl overflow-hidden" style={{ boxShadow: "0 4px 30px rgba(20,63,128,0.10)" }}>
              <Image
                src="/images/landing/coaching-session.jpg"
                alt="Career coaching session — warm, natural, engaged conversation over coffee"
                width={1440}
                height={480}
                className="w-full"
                style={{
                  objectFit: "cover",
                  objectPosition: "center center",
                }}
              />
            </div>

            {/* Two-panel comparison */}
            <CoachComparisonPanels />
          </div>
        </section>

        {/* Testimonial: After Section B */}
        <TestimonialInterstitial
          quote="I was able to get it working, thank you! It was helpful. I applied for a job yesterday and already got a response back, phone screen is scheduled for next week. Thank you for this resource!"
          name="Erin W."
        />

        {/* ================================================================
            SECTION C: EXTENSION AUTOFILL
            Background: warm gray (#f5f7fa)
        ================================================================ */}
        <section
          id="extension-autofill-section"
          className="px-6 md:px-12"
          style={{
            background: "#f5f7fa",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2
                className="font-bold"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  color: "#143f80",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                  maxWidth: "40ch",
                  margin: "0 auto",
                }}
              >
                Job description in. Application out.
              </h2>
              <p
                className="mt-4 mx-auto"
                style={{ color: "#475569", fontSize: "1.05rem", maxWidth: "52ch" }}
              >
                The Hirecarta Chrome Extension reads the job posting, tailors your resume and cover letter, and fills the form.
              </p>
            </div>

            <LandingExtensionFlow />
          </div>
        </section>

        {/* ================================================================
            SECTION C.2: APPLICATION TRACKER
            Background: white
        ================================================================ */}
        <section
          id="application-tracker-section"
          className="px-6 md:px-12"
          style={{
            background: "#ffffff",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2
                className="font-bold"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  color: "#143f80",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                  maxWidth: "40ch",
                  margin: "0 auto",
                }}
              >
                Retire your job search spreadsheet.
              </h2>
              <p
                className="mt-4 mx-auto"
                style={{ color: "#475569", fontSize: "1.05rem", maxWidth: "52ch" }}
              >
                Every application is logged the moment you apply. Statuses, documents, and follow-up reminders stay organized without you updating a thing.
              </p>
            </div>

            <LandingTrackerBoard />

            {/* CTA */}
            <div className="mt-12 text-center">
              <GoldCtaButton
                label="Start Free (No Credit Card)"
                eventLabel="extension_autofill_cta_button"
                buttonPosition="extension_autofill_section"
                heroVariant={heroVariant}
              />
            </div>
          </div>
        </section>

        {/* Testimonial: After Section C */}
        <TestimonialInterstitial
          quote="The website is well done, simple but effective. The UI is clear. Overall I think it is one of the best web apps on the market to handle job applications."
          name="Giraldi C."
        />

        {/* ================================================================
            SECTION D: BENTO FEATURES GRID
            Background: cool off-white (#f4f6f9)
        ================================================================ */}
        <section
          id="bento-features-section"
          className="px-6 md:px-12"
          style={{
            background: "#f4f6f9",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2
                className="font-bold"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  color: "#143f80",
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                }}
              >
                Most tools help you apply. Hirecarta helps you get hired.
              </h2>
              <p
                className="mt-4 mx-auto"
                style={{ color: "#475569", fontSize: "1.05rem", maxWidth: "55ch" }}
              >
                One profile. One agent. Every stage, from first application to accepted offer.
              </p>
            </div>

            {/* Dynamic Bento Grid (Responsive) */}
            <div className="mt-8">
              <LandingBento />
            </div>
          </div>
        </section>

        {/* ================================================================
            SECTION D.5: FOUNDER VIDEO
            Background: white
        ================================================================ */}
        <section
          id="founder-video-section"
          className="px-6 md:px-12"
          style={{
            background: "#ffffff",
            paddingTop: "6rem",
            paddingBottom: "6rem",
          }}
        >
          <div className="max-w-2xl mx-auto text-center">
            <p
              className="italic mb-6"
              style={{ color: "#94a3b8", fontSize: "0.9rem" }}
            >
              A note from the founder
            </p>

            <FounderVideoPlayer />

            <div className="mt-6">
              <p className="font-bold" style={{ color: "#143f80" }}>Chester Liu</p>
              <p className="text-sm mt-0.5" style={{ color: "#94a3b8" }}>Founder, Hirecarta</p>
              <p
                className="italic mt-4 mx-auto text-sm leading-relaxed"
                style={{ color: "#64748b", maxWidth: "40ch" }}
              >
                &ldquo;I read every support message personally. This product exists because I&apos;ve been in your shoes.&rdquo;
              </p>
            </div>
          </div>
        </section>

        {/* Testimonial: After Section D.5 */}
        <TestimonialInterstitial
          quote="I really like Hirecarta and it is part of my job posting analysis process. It is really great to have a tool that can look at a job description and point out areas of concern that I might have looked past during my initial review."
          name="John K."
        />

        {/* ================================================================
            SECTION E: RESUME CHECK CTA BANNER
            Background: cool off-white (#f4f6f9)
        ================================================================ */}
        <section
          id="resume-check-banner-section"
          className="px-6 md:px-12"
          style={{
            background: "#f4f6f9",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-xl mx-auto text-center">
            <h2
              className="font-bold mb-5"
              style={{
                fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
                color: "#143f80",
                letterSpacing: "-0.015em",
                lineHeight: 1.25,
              }}
            >
              See exactly why you&apos;re not getting called back.
            </h2>
            <p
              className="mb-8 leading-relaxed"
              style={{ color: "#475569", fontSize: "1.05rem", maxWidth: "48ch", margin: "0 auto 2rem" }}
            >
              Paste your resume and any job description. In seconds, your agent scores the match and tells you exactly what&apos;s missing, in plain English.
            </p>
            <Link
              href="/resume-check"
              className="inline-flex items-center justify-center rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg bg-accent hover:bg-[#b87f00] text-white"
              style={{
                fontSize: "1.1rem",
                height: "3.25rem",
                paddingLeft: "2.5rem",
                paddingRight: "2.5rem",
                boxShadow: "0 2px 12px rgba(214,148,0,0.30)",
              }}
              onClick={() =>
                trackEvent("cta_click", {
                  event_category: "conversion",
                  event_label: "resume_check_cta_button",
                  page_location: window.location.href,
                  button_position: "resume_check_banner_section",
                })
              }
            >
              Analyze My Resume Free →
            </Link>
          </div>
        </section>

        {/* ================================================================
            SECTION F: THE CLOSE
            Background: steel blue full-bleed (#143f80)
        ================================================================ */}
        <section
          id="final-cta-section"
          className="px-6 md:px-12"
          style={{
            background: "#143f80",
            paddingTop: "7rem",
            paddingBottom: "7rem",
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2
              className="font-bold mb-5"
              style={{
                fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
                color: "#ffffff",
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              You&apos;ve been working harder than the people getting hired.
              <br />
              It&apos;s time to work smarter.
            </h2>
            <p
              className="mb-10"
              style={{ color: "rgba(255,255,255,0.75)", fontSize: "1.1rem" }}
            >
              Your agent is ready. Your competition&apos;s isn&apos;t.
            </p>
            <GoldCtaButton
              label="Start Free (No Credit Card)"
              eventLabel="final_cta_button"
              buttonPosition="final_section"
              heroVariant={heroVariant}
            />
          </div>
        </section>

        {/* Upcoming Events */}
        <UpcomingEventsSection />

        <Footer />
      </div>
    </div>
  );
}
